import React from 'react';
import { View, Text, StyleSheet, Image, Font } from '@react-pdf/renderer';
import DocumentPdfLayout from './DocumentPdfLayout';
import { TFunction } from 'i18next';
import { FormTemplateWithSections } from '../../services/apiSLTP/qhse/forms/formTemplates';
import { FormResponseWithDetails } from '../../services/apiSLTP/qhse/forms/formResponses';
import { FormResponseDocument } from '../../services/apiSLTP/qhse/forms/formResponseDocuments';
import { FormResponseUserWithDetails } from '../../services/apiSLTP/qhse/forms/formResponseUsers';
import { FormQuestionWithOptions } from '../../services/apiSLTP/qhse/forms/formQuestions';
import { capitalizeFirstLetter, formatName } from '../../utils/capitalize';
import { getContractTypeLabel } from '../../constants/labels/user';
import { useTheme } from '../../hooks/useTheme';

interface FormResponsePDFProps {
    formTemplate: FormTemplateWithSections;
    formResponse: FormResponseWithDetails;
    documents?: FormResponseDocument[];
    attendees?: FormResponseUserWithDetails[];
    t: TFunction<'translation', undefined>;
}

const styles = StyleSheet.create({
    page: { display: 'flex', flexDirection: 'column', gap: 10 },
    section: {},
    sectionTitle: { fontSize: 12, fontWeight: 'bold', backgroundColor: '#eee', padding: 10, textTransform: 'uppercase' },
    questionsContainer: { display: 'flex', flexDirection: 'column', gap: 5, padding: 10 },
    questionRow: {},
    questionText: { fontSize: 10, fontWeight: 'bold' },
    answerText: { fontSize: 10, padding: 5 },
    answerObservations: { fontSize: 8, padding: 5, color: '#666' },
    subSection: {},
    attendeeRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: 5,
        borderBottom: '1px solid #ddd',
    },
    attendeeInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    attendeeName: {
        fontSize: 10,
        fontWeight: 'bold',
    },
    attendeeContract: {
        fontSize: 9,
        color: '#666',
    },
    validated: {
        fontSize: 10,
        fontWeight: 'bold',
        color: 'lightgreen',
    },
    notValidated: {
        fontSize: 9,
        fontStyle: 'italic',
        color: '#666',
    },
    external: {
        fontSize: 9,
        color: 'royalblue',
    },
    documentsContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 5,
        padding: 10,
    },
});

const FormResponsePDF: React.FC<FormResponsePDFProps> = ({ formTemplate, formResponse, documents = [], attendees = [], t }) => {
    const getAnswersForQuestion = (question: FormQuestionWithOptions): string[] => {
        const answer = formResponse.answers.find((a) => a.questionId === question._id);
        if (!answer) return [];
        if (Array.isArray(answer.value)) {
            return answer.value.filter((v) => v !== null && v !== undefined && v !== '');
        }
        const stringValue = String(answer.value);
        return stringValue ? [stringValue] : [];
    };

    const getObservationsForQuestion = (question: FormQuestionWithOptions): string => {
        const answer = formResponse.answers.find((a) => a.questionId === question._id);
        if (!answer?.observations) return '';
        return answer.observations;
    };

    return (
        <DocumentPdfLayout
            fileName={`${formTemplate.title} - ${formResponse.submittedAt || formResponse.createdAt}`}
            title={formTemplate.title}
            locale={t('locale')}
        >
            <View style={styles.page}>
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>{capitalizeFirstLetter(t('generalInformation'))}</Text>
                    <View style={styles.questionsContainer}>
                        <View style={styles.questionRow} wrap={false}>
                            <Text style={styles.questionText}>{capitalizeFirstLetter(t('date'))}</Text>
                            <Text style={styles.answerText}>
                                {formResponse.submittedAt
                                    ? new Date(formResponse.submittedAt).toLocaleDateString(t('locale'))
                                    : new Date(formResponse.createdAt).toLocaleDateString(t('locale'))}
                            </Text>
                        </View>
                        <View style={styles.questionRow} wrap={false}>
                            <Text style={styles.questionText}>{capitalizeFirstLetter(t('filledBy'))}</Text>
                            <Text style={styles.answerText}>{formatName(formResponse.filledBy.firstName, formResponse.filledBy.lastName)}</Text>
                        </View>
                        {formTemplate.isSiteEnabled && !!formResponse.siteId && (
                            <View style={styles.questionRow} wrap={false}>
                                <Text style={styles.questionText}>{capitalizeFirstLetter(t('site'))}</Text>
                                <Text style={styles.answerText}>{formResponse.siteId.name}</Text>
                            </View>
                        )}
                    </View>
                </View>

                {attendees.length > 0 && (
                    <View style={styles.section} wrap={false}>
                        <Text style={styles.sectionTitle}>{capitalizeFirstLetter(t('attendees'))}</Text>
                        <View style={styles.questionsContainer}>
                            {attendees.map((user, i) => {
                                const userName = user.name || formatName(user.userId?.firstName, user.userId?.lastName) || '';
                                const hasValidated = user.hasValidatedPresence;

                                return (
                                    <View key={i} style={styles.attendeeRow} wrap={false}>
                                        <View style={styles.attendeeInfo}>
                                            <Text style={styles.attendeeName}>{userName}</Text>
                                            <Text style={styles.attendeeContract}>{getContractTypeLabel(user.contractType).toUpperCase()}</Text>
                                        </View>

                                        {hasValidated ? (
                                            <Text style={styles.validated}>{capitalizeFirstLetter(t('signed'))}</Text>
                                        ) : user.name ? (
                                            <Text style={styles.external}>{capitalizeFirstLetter(t('external'))}</Text>
                                        ) : (
                                            <Text style={styles.notValidated}>{capitalizeFirstLetter(t('notSigned'))}</Text>
                                        )}
                                    </View>
                                );
                            })}
                        </View>
                    </View>
                )}

                {documents.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>{capitalizeFirstLetter(t('documents'))}</Text>
                        <View style={styles.documentsContainer}>
                            {documents.map(
                                (doc, i) =>
                                    doc.file?.url && (
                                        <Image
                                            key={i}
                                            src={{
                                                uri: doc.file.url,
                                                method: 'GET',
                                                body: null,
                                                headers: { 'Cache-Control': 'no-cache' },
                                            }}
                                            style={{ width: 150, height: 150, objectFit: 'contain', marginBottom: 5 }}
                                        />
                                    )
                            )}
                        </View>
                    </View>
                )}

                {formTemplate.sections.map((section, sectionIndex) => (
                    <View key={sectionIndex} style={styles.section}>
                        <Text style={styles.sectionTitle} wrap={false}>
                            {section.title}
                        </Text>
                        <View style={styles.questionsContainer}>
                            {section.questions.map((q, qIndex) => {
                                const answers = getAnswersForQuestion(q);
                                const observations = getObservationsForQuestion(q);

                                return (
                                    <View key={qIndex} style={styles.questionRow} wrap={false}>
                                        <Text style={styles.questionText}>{q.title}</Text>
                                        {answers.length === 0 ? (
                                            <Text style={styles.answerText}>-</Text>
                                        ) : (
                                            answers.map((answer, index) => (
                                                <Text key={index} style={styles.answerText}>
                                                    {answer || '-'}
                                                </Text>
                                            ))
                                        )}
                                        {observations && (
                                            <Text style={styles.answerObservations}>
                                                {capitalizeFirstLetter(t('observations'))}: {observations}
                                            </Text>
                                        )}
                                    </View>
                                );
                            })}
                        </View>
                    </View>
                ))}

                {formResponse.observations && (
                    <View style={styles.section} break={false}>
                        <Text style={styles.sectionTitle}>{t('observations')}</Text>
                        <Text style={styles.answerText}>{formResponse.observations}</Text>
                    </View>
                )}
            </View>
        </DocumentPdfLayout>
    );
};

export default FormResponsePDF;
